import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Card } from "./Card.js";
import { sortHand, suitOf } from "../lib/cards.js";
import { haptic } from "../lib/telegram.js";
import type { DurakMove, DurakView } from "../lib/views.js";

interface Props {
  view: DurakView;
  onMove(move: DurakMove): void;
}

/**
 * Стол дурака.
 *
 * Управление рассчитано на палец: тап по карте выбирает её, тап по нужной
 * паре на столе кладёт. Если ход однозначен — играется сразу, без второго тапа.
 */
export function DurakTable({ view, onMove }: Props) {
  const [selected, setSelected] = useState<number | null>(null);

  const myTurn = view.turn === view.me;
  const iDefend = view.defender === view.me;

  // Сбрасываем выбор, когда стол поменялся под руками.
  useEffect(() => {
    setSelected(null);
  }, [view.table.length, view.phase, view.turn]);

  const hand = useMemo(
    () => sortHand(view.myHand, view.trumpSuitIndex),
    [view.myHand, view.trumpSuitIndex],
  );

  /** Карты, которыми сейчас можно пойти. */
  const playable = useMemo(() => {
    const set = new Set<number>();
    if (!myTurn) return set;
    for (const m of view.legalMoves) {
      if (m.type === "attack" || m.type === "defend") set.add(m.card);
    }
    return set;
  }, [view.legalMoves, myTurn]);

  /** Слоты, которые выбранная карта может побить. */
  const targets = useMemo(() => {
    if (selected === null) return new Set<number>();
    const set = new Set<number>();
    for (const m of view.legalMoves) {
      if (m.type === "defend" && m.card === selected) set.add(m.slot);
    }
    return set;
  }, [view.legalMoves, selected]);

  const canTake = myTurn && view.legalMoves.some((m) => m.type === "take");
  const canPass = myTurn && view.legalMoves.some((m) => m.type === "pass");

  function tapCard(card: number) {
    if (!playable.has(card)) return;
    haptic.select();

    const attack = view.legalMoves.find((m) => m.type === "attack" && m.card === card);
    const defences = view.legalMoves.filter((m) => m.type === "defend" && m.card === card);

    // Атака однозначна — кладём сразу.
    if (attack && defences.length === 0) {
      haptic.move();
      onMove(attack);
      setSelected(null);
      return;
    }
    // Защита, и бить можно ровно одну карту — тоже без второго тапа.
    if (!attack && defences.length === 1) {
      haptic.move();
      onMove(defences[0]!);
      setSelected(null);
      return;
    }
    setSelected((cur) => (cur === card ? null : card));
  }

  function tapSlot(index: number) {
    if (selected === null || !targets.has(index)) return;
    haptic.move();
    onMove({ type: "defend", card: selected, slot: index });
    setSelected(null);
  }

  const hint = myTurn
    ? iDefend
      ? selected !== null
        ? "Выбери карту на столе, которую бьёшь"
        : "Отбивайся или бери"
      : view.table.length === 0
        ? "Твой ход — заходи"
        : "Можешь подкинуть или сказать «бито»"
    : "Ход соперника";

  return (
    <div className="durak">
      {/* рука соперника — рубашкой вверх */}
      <div className="durak-oppo">
        {Array.from({ length: view.opponentHandCount }).map((_, i) => (
          <Card
            key={i}
            card={null}
            small
            style={{
              marginLeft: i === 0 ? 0 : oppoOverlap(view.opponentHandCount),
              transform: `rotate(${fanAngle(i, view.opponentHandCount) * -0.6}deg)`,
            }}
          />
        ))}
      </div>

      {/* колода, козырь и отбой */}
      <div className="durak-side">
        <div className="durak-deck">
          {view.trumpCard !== null && (
            <div className="durak-trump">
              <Card card={view.trumpCard} small />
            </div>
          )}
          {view.deckCount > 0 && (
            <div className="durak-deck-stack">
              <Card card={null} small />
              <span className="durak-count">{view.deckCount}</span>
            </div>
          )}
        </div>
        <div className="durak-meta">
          <span className="durak-trump-suit">козырь {suitOf(view.trumpCard ?? 0)}</span>
          {view.discardCount > 0 && <span className="muted small">отбой {view.discardCount}</span>}
        </div>
      </div>

      {/* стол */}
      <div className="durak-table">
        <AnimatePresence mode="popLayout">
          {view.table.length === 0 ? (
            <motion.div
              key="empty"
              className="durak-empty muted small"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {myTurn && !iDefend ? "Выбери карту" : "Стол пуст"}
            </motion.div>
          ) : (
            view.table.map((slot, i) => (
              <motion.div
                key={`${slot.attack}`}
                className={`durak-pair${targets.has(i) ? " is-target" : ""}`}
                onClick={() => tapSlot(i)}
                initial={{ opacity: 0, y: -14, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ type: "spring", stiffness: 460, damping: 34 }}
              >
                <Card card={slot.attack} small layoutId={`card-${slot.attack}`} />
                {slot.defence !== null && (
                  <div className="durak-defence">
                    <Card card={slot.defence} small layoutId={`card-${slot.defence}`} />
                  </div>
                )}
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      <div className="durak-hint muted small">{hint}</div>

      {/* моя рука */}
      <div className="durak-hand">
        <AnimatePresence mode="popLayout">
          {hand.map((card, i) => (
            <Card
              key={card}
              card={card}
              layoutId={`card-${card}`}
              playable={playable.has(card)}
              selected={selected === card}
              onClick={() => tapCard(card)}
              style={{
                marginLeft: i === 0 ? 0 : handOverlap(hand.length),
                zIndex: selected === card ? 50 : i,
              }}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* действия */}
      <div className="durak-actions">
        {canTake && (
          <button
            className="btn btn-danger"
            onClick={() => {
              haptic.warn();
              onMove({ type: "take" });
            }}
          >
            Беру
          </button>
        )}
        {canPass && (
          <button
            className="btn btn-primary"
            onClick={() => {
              haptic.tap();
              onMove({ type: "pass" });
            }}
          >
            {iDefend ? "Пас" : view.phase === "throwin" ? "Хватит" : "Бито"}
          </button>
        )}
      </div>
    </div>
  );
}

/** Чем больше карт, тем сильнее они наезжают друг на друга. */
function handOverlap(count: number): number {
  if (count <= 6) return -14;
  if (count <= 9) return -26;
  if (count <= 12) return -34;
  return -40;
}

function oppoOverlap(count: number): number {
  if (count <= 6) return -18;
  if (count <= 10) return -26;
  return -32;
}

/** Лёгкий веер: середина ровно, края чуть развёрнуты. */
function fanAngle(i: number, count: number): number {
  if (count < 2) return 0;
  const mid = (count - 1) / 2;
  return ((i - mid) / mid) * 6;
}
